import { useRef, useState } from 'react'

/**
 * Arrastar para reordenar, com Pointer Events.
 *
 * ## Por que não uma biblioteca
 *
 * `dnd-kit` são ~30 KB num bundle que já passa de 1 MB, e o que este caso
 * precisa é de uma lista vertical, um eixo, sem colunas nem aninhamento. O
 * custo real de arrastar não é a biblioteca: é que **jsdom não tem layout**, e
 * `getBoundingClientRect` devolve zero. Nenhuma escolha aqui muda isso — o
 * gesto se verifica no Playwright, e o que dá para testar em unidade é o
 * cálculo puro do destino, que mora em `moveTo`.
 *
 * ## Pointer Events, com os ouvintes no `window` — e não `setPointerCapture`
 *
 * Um só código para mouse, dedo e caneta. O arrasto precisa sobreviver quando o
 * dedo sai de cima da alça, e a primeira versão usava `setPointerCapture` para
 * isso. Ela quebrou no navegador: a chamada lança `NotFoundError` quando o
 * ponteiro daquele id não está mais ativo, e o `throw` matava o gesto inteiro
 * antes de o estado ser criado.
 *
 * Ouvir `pointermove`/`pointerup` no `window` enquanto dura o arrasto resolve o
 * mesmo problema sem poder falhar, e ainda pega o caso em que o dedo sai da
 * janela — que a captura também não cobre.
 *
 * ## Os retângulos são lidos UMA VEZ, no início
 *
 * Durante o arrasto a lista não muda de tamanho — só o item fantasma se move.
 * Medir a cada `pointermove` seria forçar layout dezenas de vezes por segundo
 * para ler os mesmos números. E lendo uma vez, o hook não precisa de uma `ref`
 * por linha: procura `[data-item-id]` dentro do container e pronto.
 *
 * ## O deslocamento sai das ALTURAS medidas, e não de um número fixo
 *
 * As linhas não têm todas a mesma altura: a de grupo tem segunda linha, a de
 * texto livre não. Abrir o espaço com uma constante deixaria a lista tremendo
 * — cada troca erraria pela diferença. Com as alturas reais, o buraco que se
 * abre é exatamente do tamanho do item que saiu.
 */

type Estado = {
  id: string
  /** Índice sob o ponteiro agora — é o que abre o espaço. */
  destino: number
  origem: number
  /** O quanto o dedo já andou. O item arrastado segue isto 1:1. */
  dy: number
}

export function useArrastar(onSoltar: (id: string, destino: number) => void) {
  const container = useRef<HTMLUListElement | null>(null)
  const meios = useRef<number[]>([])
  const topos = useRef<number[]>([])
  const alturas = useRef<number[]>([])
  const inicioY = useRef(0)
  const [estado, setEstado] = useState<Estado | null>(null)

  // Em `ref` para os ouvintes de `window` sempre enxergarem o valor de agora:
  // eles são registrados uma vez, no `pointerdown`, e uma closure sobre o
  // estado veria para sempre o de então.
  const atual = useRef<Estado | null>(null)

  function comecar(_e: React.PointerEvent, id: string) {
    const lista = container.current
    if (lista === null) return

    const linhas = [...lista.querySelectorAll<HTMLElement>('[data-item-id]')]
    const origem = linhas.findIndex((l) => l.dataset['itemId'] === id)
    if (origem === -1) return

    // O meio vertical de cada linha: o ponteiro passou do meio da linha de
    // baixo, o item cai para lá. Usar a borda em vez do meio faria a troca
    // acontecer cedo demais e o item ficaria piscando entre dois lugares.
    const rets = linhas.map((l) => l.getBoundingClientRect())
    meios.current = rets.map((r) => r.top + r.height / 2)
    topos.current = rets.map((r) => r.top)
    alturas.current = rets.map((r) => r.height)
    inicioY.current = _e.clientY

    const inicial = { id, origem, destino: origem, dy: 0 }
    atual.current = inicial
    setEstado(inicial)

    function aoMover(ev: PointerEvent) {
      const agora = atual.current
      if (agora === null) return
      let destino = 0
      for (const meio of meios.current) {
        if (ev.clientY > meio) destino += 1
      }
      const limitado = Math.min(Math.max(destino, 0), meios.current.length - 1)
      const dy = ev.clientY - inicioY.current
      if (limitado === agora.destino && dy === agora.dy) return
      const proximo = { ...agora, destino: limitado, dy }
      atual.current = proximo
      setEstado(proximo)
    }

    function encerrar() {
      window.removeEventListener('pointermove', aoMover)
      window.removeEventListener('pointerup', encerrar)
      window.removeEventListener('pointercancel', encerrar)
      const fim = atual.current
      atual.current = null
      setEstado(null)
      // Só grava quando de fato mudou de lugar: um toque na alça que não
      // arrasta é um clique, e clique abre o menu.
      if (fim !== null && fim.destino !== fim.origem) onSoltar(fim.id, fim.destino)
    }

    window.addEventListener('pointermove', aoMover)
    window.addEventListener('pointerup', encerrar)
    window.addEventListener('pointercancel', encerrar)
  }

  return {
    /**
     * Callback ref, e não o objeto de ref.
     *
     * `react-hooks/refs` proíbe **ler** uma ref durante o render, e devolver o
     * objeto fazia o `ref={arrastar.container}` do JSX cair na regra. A função
     * é chamada pelo React depois do render, que é exatamente quando ler e
     * escrever ref é permitido.
     */
    refDaLista: (el: HTMLUListElement | null) => {
      container.current = el
    },
    arrastando: estado?.id ?? null,

    /**
     * Quantos pixels a linha `indice` deve deslocar agora.
     *
     * O item arrastado segue o dedo; os outros abrem espaço pulando a altura
     * **do arrastado**, que é o buraco que ele deixou. Quem está fora do
     * intervalo entre origem e destino não se mexe — mover a lista inteira
     * seria movimento sem informação.
     */
    deslocamento: (indice: number): number => {
      const e = estado
      if (e === null) return 0
      const h = alturas.current[e.origem] ?? 0

      if (indice === e.origem) return e.dy
      if (e.destino > e.origem && indice > e.origem && indice <= e.destino) return -h
      if (e.destino < e.origem && indice >= e.destino && indice < e.origem) return h
      return 0
    },

    comecar,
  }
}
